import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { AccessFlow, Header } from "@aptsi-types";
import { AccessFlowService } from "../access-flow.service";
import { MatDialog, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { DeleteDialogComponent } from "../../shared/dialogs/delete-dialog/delete-dialog.component";
import { Subject } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { KeycloakStateService } from 'src/app/core/service/utility.service';
import { GenerateAccessFlowDialogComponent } from "../generate-access-flow-dialog/generate-access-flow-dialog.component";
import { SuccessDialogComponent } from "../../shared/dialogs/success-dialog/success-dialog.component";
import { ErrorDialogComponent } from "../../shared/dialogs/error-dialog.component";
import { EnvironmentService } from "src/app/core/service/environment.service";

interface AccessFlowCheckResponse {
  canGenerate: boolean;
  message: string;
}

@Component({
  selector: "app-access-flow-search",
  templateUrl: "./access-flow-search.component.html",
  styleUrls: ["./access-flow-search.component.scss"],
})
export class AccessFlowSearchComponent implements OnInit {
  // ─── Pagination ───────────────────────────────────────────────
  currentPage = 1;
  itemsPerPage = 7;
  totalPages = 0;
  totalPagesNormal = 0;
  totalPagesSearch = 0;

  // ─── Search & Filtering ──────────────────────────────────────
  searchText: string = "";
  filterByHeader: string = "all";
  isSearch: boolean = false;
  searchTextChanged = new Subject<string>();

  // ─── Data ─────────────────────────────────────────────────────
  filteredData: AccessFlow[] = [];
  searchedData: AccessFlow[] = [];
  accessFlow: AccessFlow[] = [];
  organizationId: string;
  checkResult: AccessFlowCheckResponse | null = null;

  // ─── UI Configuration ─────────────────────────────────────────
  headers: Header[] = [
    {
      name: "Access Control Flow Id",
      property: "accessflowid",
      showInDropdown: true,
      description: "Unique identifier for the access control flow",
    },
    {
      name: "Access Control Flow Name",
      property: "name",
      showInDropdown: true,
      description: "Name of the access control flow",
    },
    {
      name: "Asset Id",
      property: "assetid",
      showInDropdown: true,
      description: "Unique identifier for the associated asset",
    },
    {
      name: "Active",
      property: "isActive",
      showInDropdown: true,
      description: "Indicates whether the access control flow is active",
    },
    {
      name: "Last Updated",
      property: "timestamp",
      showInDropdown: true,
      description: "Timestamp of the most recent update to the access control flow",
    },
  ];

  buttonConfig = {
    update: true,
    delete: true,
    capture: false,
  };

  // ─── Constructor ─────────────────────────────────────────────
  constructor(
    private router: Router,
    private accessFlowService: AccessFlowService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private keycloakStateService: KeycloakStateService,
    public environmentService: EnvironmentService 
  ) { }

  // ─── Lifecycle ───────────────────────────────────────────────
  async ngOnInit(): Promise<void> {

    this.organizationId = this.keycloakStateService.getOrganizationId();
    await this.fetchAccessFlow();
    this.searchTextChanged
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => this.searchAccessFlow());
    this.cdr.detectChanges();
  }

  // ─── Getters ─────────────────────────────────────────────────
  get paginatedData(): AccessFlow[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.isSearch
      ? this.searchedData.slice(start, end)
      : this.filteredData.slice(start, end);
  }

  openGenerateModal() {
    if (!this.organizationId) {
      alert('Organization ID is not available');
      return;
    }

    const dialogRef = this.dialog.open(GenerateAccessFlowDialogComponent, {
      width: '1000px',
      data: { title: 'Auto-Generate Access Flow', message: 'Check whether all assets have accessflow', organizationId: this.organizationId }
    });

    dialogRef.afterClosed().subscribe(result => {
      this.fetchAccessFlow();
    });
  }


  private loadData() {
    // Implement logic to fetch updated data for the table
    // Example: this.http.get('/api/access-flows').subscribe(data => this.filteredData = data);
  }

  // ─── Event Handlers ──────────────────────────────────────────
  changePage(page: number): void {
    this.currentPage = page;
    this.totalPages = this.isSearch ? this.totalPagesSearch : this.totalPagesNormal;
    this.isSearch ? this.fetchAccessFlowSearch() : this.fetchAccessFlow();
  }

  onSearchTextChange(searchValue: string): void {
    this.searchTextChanged.next(searchValue);
  }

  handleAction(event: { action: string; rowIndex: number; rowData: any }): void {
    if (event.action === "update") {
      this.updateAccessFlow(event.rowIndex);
    } else if (event.action === "delete") {
      this.deleteAccessFlow(event.rowIndex);
    }
  }

  // ─── Navigation ──────────────────────────────────────────────
  updateAccessFlow(index: number): void {
    const accessflowid = this.isSearch
      ? this.searchedData[index].accessflowid
      : this.filteredData[index].accessflowid;

    this.router.navigate(["ada-access/auth-tools/access-flow/update", accessflowid]);
  }

  create(): void {
    this.router.navigate(["ada-access/auth-tools/access-flow/create"]);
  }

  back(): void {
    if (this.isSearch) {
      this.isSearch = false;
      this.fetchAccessFlow();
    } else {
      this.router.navigate(["ada-access/auth-tools"]);
    }
  }

  // ─── Data Fetching ───────────────────────────────────────────
  async fetchAccessFlow(): Promise<void> {
    try {
      const response = await this.accessFlowService
        .getAccessFlows(this.currentPage - 1)
        .toPromise();

      this.filteredData = response.map(flow => ({
        ...flow,
        timestamp: this.formatDate(flow.timestamp),
      }));

      this.updatePagination(response, false);
    } catch (error) {
      console.error("Error fetching AccessFlow:", error);
    }
  }

  async fetchAccessFlowSearch(): Promise<void> {
    try {
      const response = await this.accessFlowService
        .getAccessFlowSearch(this.currentPage - 1, this.searchText, this.filterByHeader)
        .toPromise();

      this.searchedData = response.map(flow => ({
        ...flow,
        timestamp: this.formatDate(flow.timestamp),
      }));

      this.updatePagination(response, true);
    } catch (error) {
      console.error("Error fetching AccessFlow (search):", error);
    }
  }

  private updatePagination(response: any[], isSearch: boolean): void {
    if (response.length > 0 && response[0].totalPages) {
      const totalPages = response[0].totalPages;
      const currentPage = response[0].currentPage + 1;

      if (isSearch) {
        this.totalPagesSearch = totalPages;
      } else {
        this.totalPagesNormal = totalPages;
      }

      this.totalPages = totalPages;
      this.currentPage = currentPage;
    }
  }

  // ─── Search ──────────────────────────────────────────────────
  // async searchAccessFlow(): Promise<void> {
  //   try {
  //     if (this.searchText) {
  //       this.isSearch = true;
  //       this.currentPage = 1;

  //       await this.fetchAccessFlowSearch();
  //       this.totalPages = this.totalPagesSearch;

  //       const selectedHeader = this.headers.find(
  //         (h) => h.property === this.filterByHeader && h.showInDropdown
  //       );

  //       if (selectedHeader) {
  //         this.searchedData = this.searchedData
  //           .filter((flow) => {
  //             const value = flow[selectedHeader.property];
  //             return value
  //               ?.toString()
  //               .toLowerCase()
  //               .includes(this.searchText.toLowerCase());
  //           })
  //           .sort((a, b) =>
  //             a[selectedHeader.property]
  //               ?.toString()
  //               .localeCompare(b[selectedHeader.property]?.toString())
  //           );
  //       } else {
  //         this.filteredData = [];
  //       }
  //     } else {
  //       this.isSearch = false;
  //       this.currentPage = 1;
  //       await this.fetchAccessFlow();
  //     }
  //   } catch (error) {
  //     console.error("Error during search:", error);
  //   }
  // }

  async searchAccessFlow(): Promise<void> {

    try {

      if (this.searchText?.trim()) {

        this.isSearch = true;
        this.currentPage = 1;

        if (!this.filterByHeader) {
          this.filterByHeader = 'all';
        }

        // Backend handles:
        // search
        // pagination
        // filtering
        await this.fetchAccessFlowSearch();

      } else {

        this.isSearch = false;
        this.currentPage = 1;

        await this.fetchAccessFlow();
      }

    } catch (error) {

      console.error(
        'Error during search:',
        error
      );
    }
  }

  // ─── Delete ──────────────────────────────────────────────────
  async deleteAccessFlow(index: number): Promise<void> {
    try {
      const dialogRef = this.dialog.open(DeleteDialogComponent, {
        data: {
          title: "Are you sure?",
          message: "Do you really want to delete this Access Flow?",
          isConfirm: false,
        },
      });

      const result = await dialogRef.afterClosed().toPromise();
      if (!result) return;

      const accessflowid = this.isSearch
        ? this.searchedData[index].accessflowid
        : this.filteredData[index].accessflowid;

      await this.accessFlowService.deleteAccessFlow(accessflowid).toPromise();

      this.dialog.open(DeleteDialogComponent, {
        data: {
          title: "Successful",
          message: "The Access Flow has been deleted successfully.",
          isConfirm: true,
        },
      });

      this.isSearch ? this.fetchAccessFlowSearch() : this.fetchAccessFlow();
    } catch (error) {
      console.error("Error deleting Access Flow:", error);
    }
  }

  // ─── Utility ─────────────────────────────────────────────────
  formatDate(isoString: string): string {
    if (!isoString) return "";
    const date = new Date(isoString);
    return date
      .toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(",", "");
  }

  openSuccessDialog(isCreate: boolean): void {
    const dialogRef = this.dialog.open(SuccessDialogComponent, {
      width: "250px",
      data: { isCreate: isCreate, title: "Acces Flow" },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.router.navigate(["ada-access/auth-tools/access-flow/search"]);
    });
  }

  openErrorDialog(errorMessage: string): void {
    const dialogRef = this.dialog.open(ErrorDialogComponent, {
      width: "250px",
      data: { errorMessage: errorMessage },
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.router.navigate(["ada-access/auth-tools/access-flow/search"]);
    });
  }
}
